<template>
	<div class="items-container">
		<AppSortableList ref="category-items-list" class="item-list" add-text="add_product" :items="itemsSorted" key-prop="tempid" :hasAddItem="!!categoryTempId && !multiSelectMode" v-show="!categoryTempId || !category?.collapsed" @add-item="checkCategoryAndAddItem" @sorting="sortingItems" @sorted="(evt, sortResult) => sortItems(sortResult)" create-dropdown-on-expand>
			<template v-slot:heading="{item, index}">
				<div class="item heading-item">
					<div class="handle icon-dimension-20 undraggable"></div>
					<div class="item-content">
						<div class="item-name">
							<div class="name-wrapper">
								<p>{{ getString('product_name') }}</p>
							</div>
						</div>
						<div class="item-price">
							<p class="text-align-right">{{ getString('price_symbol', [currencyStore.currCurrency?.symbol]) }}</p>
						</div>
					</div>
					<div class="item-actions heading-item-actions"></div>
				</div>
			</template>
			<template v-slot:item-header="{item, index}">
				<div class="item" @click.stop="clickMenuItemMultiselect(item)" :class="{active: item.selected}">
					<div v-if="!multiSelectMode" class="handle icon-dimension-20" :class="{undraggable: item.editActive}"><img :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="handle"></div>
					<AppCheckbox v-if="multiSelectMode" :is-active="item.selected" :auto-toggle="false" class="checkbox"/>
					<div class="item-content" @click="clickMenuItem(item, index)">
						<div class="item-name">
							<div class="name-wrapper">
								<p>{{ item.name }}</p>
							</div>
						</div>
						<div class="item-price" :class="{discount: hasDiscountedPrice(item)}">
							<p class="discounted-price" :title="getString('price_without_discount')" v-show="hasDiscountedPrice(item)">{{ currencyStore.readableCurrencyValue(item.full_price, true, false) }}</p>
							<p>{{ currencyStore.readableCurrencyValue(item.price, true, false) }}</p>
						</div>
					</div>
					<div class="item-actions">
						<AppButtonIcon v-show="!multiSelectMode" prevent-change :ref="'item-open-button-'+item.tempid" :active="item.editActive" icon-name="icon-edit-white" small @click-icon="clickMenuItem(item, index)"/>
						<AvailabilityDot :class="{visible: item.available === false || item.available === null}" :type="availabilityType(item)"/>
					</div>
				</div>
			</template>
			<template v-slot:item-dropdown="{item, index}">
				<ItemEditor :ref="'item-editor-'+item.tempid" :categoryTempId="categoryTempId" :itemTempId="item.tempid" @item-saved="(v, closeDropdown) => savedItem(v, index, closeDropdown)"/>
			</template>
		</AppSortableList>
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore, useNotificationStore, useScriptStore, useSvgStore} from "@/shared/stores";
import {useMenuStore} from "@/console-new/stores";

import AppSortableList from "@/shared/components/AppSortableList.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import ItemEditor from "@/console-new/components/config/menu/ItemEditor.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import {useUserStore} from "@/console-new/stores";

export default {
	emits: ["sorting", "sorted", "item-saved", "category-saved"],
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let scriptStore = useScriptStore();
		let svgStore = useSvgStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, getIcon: svgStore.get, menuStore, loadScript: scriptStore.loadScript, currencyStore, showNotification: notificationStore.showNotification, userStore};
	},
	inject: ["providedAction"],
	props: {
		categoryTempId: {required: true, validator(value) {
			return value === null || (typeof value === "string" && value.length > 0);
		}},
		variationsOnly: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	name: "MenuEditorItems",
	components: {AvailabilityDot, ItemEditor, AppButtonIcon, AppSortableList, AppCheckbox},
	data() {
		return {
			scrollItemTimeout: null,
			multiSelectMode: false,
		}
	},
	computed: {
		category() {
			if(this.categoryTempId) {
				return this.menuStore.modMenu.categories[this.categoryTempId];
			} else {
				return null;
			}
		},
		itemsSorted() {
			let finalItems = [];
			if(!this.categoryTempId || !this.category?.collapsed) {
				if(this.categoryTempId) {
					finalItems = Object.values(this.category?.items).sort((a, b) => a.order_number - b.order_number);
				} else {
					finalItems = Object.values(this.menuStore.modMenu.orphanItems).sort((a, b) => a.order_number - b.order_number);
				}
			}
			let firstItem = {
				title: 'name',
				customSlot: 'heading',
				noSort: true
			}
			if(finalItems?.length) {
				finalItems = [firstItem, ...finalItems];
			}
			return finalItems;
		},
	},
	methods: {
		hasDiscountedPrice(item) {
			return item && !!item.full_price && item.full_price > 0 && item.full_price !== item.price;
		},
		clickMenuItemMultiselect(item) {
			if(this.multiSelectMode) {
				item.selected = !item.selected;
			}
		},
		clickMenuItem(item, index) {
			if(!this.multiSelectMode) {
				let forceClose = item.active;
				this.forceTriggerItem(item, index, forceClose);
			}
		},
		sortItems(sortedItems) {
			for(let i in sortedItems) {
				let itemTempId = sortedItems[i].tempid;
				if(this.categoryTempId) {
					if(this.menuStore.modMenu.categories[this.categoryTempId].items.hasOwnProperty(itemTempId)) {
						this.menuStore.modMenu.categories[this.categoryTempId].items[itemTempId].order_number = Number(i);
					}
				} else {
					if(this.menuStore.modMenu.orphanItems.hasOwnProperty(itemTempId)) {
						this.menuStore.modMenu.orphanItems[itemTempId].order_number = Number(i);
					}
				}
			}
			this.$emit("sorted");
		},
		forceTriggerItem(item, index, forceClose = false) {
			item.editActive = forceClose ? false : !item.active;
			this.triggerItem(item, index);
		},
		savedItem(item, index, closeDropdown) {
			this.$emit("item-saved", item);
			if(closeDropdown) {
				this.forceTriggerItem(item, index, true);
			}
		},
		triggerItem(item, index) {
			let isOpening = !item.active;
			if(this.$refs["category-items-list"]) {
				this.$refs["category-items-list"].toggleDropdownDelayed(index);
			}
			let openItemHeight = 0;

			let items = this.categoryTempId ? this.menuStore.modMenu.categories[this.categoryTempId].items : this.menuStore.modMenu.orphanItems;
			Object.entries(items).forEach(([i, checkItem]) => {
				let aboveItem = this.itemsSorted.indexOf(checkItem) < index;
				if(aboveItem && checkItem.active) {
					openItemHeight += this.expandedItemDropdownHeight(checkItem.tempid);
				}
				if (item.tempid !== checkItem.tempid) {
					checkItem.editActive = false;
				} else {
					checkItem.editActive = isOpening;
				}
			});
			if(isOpening) {
				let topOffset = 0;
				if(this.variationsOnly) {
					let firstMenuContainer = document.querySelector('.menu-wrapper-card');
					if (firstMenuContainer && firstMenuContainer?.offsetHeight) {
						topOffset = firstMenuContainer?.offsetHeight;
					}
				}
				this.scrollItemToTop(item.tempid, openItemHeight, topOffset)
			}
		},
		sortingItems(evt) {
			let itemTempId = evt.item.getAttribute('data-index');
			let items = this.categoryTempId ? this.menuStore.modMenu.categories[this.categoryTempId].items : this.menuStore.modMenu.orphanItems;
			let item = items[itemTempId];
			if(item.active) {
				this.$refs["category-items-list"].toggleDropdownDelayed(evt.oldIndex);
			}
			this.$emit("sorting", item);
		},
		checkCategoryAndAddItem() {

			if(this.categoryTempId && !this.category.menu_category_id) {

				let self = this;
				this.menuStore.saveCategories(function (success, details) {
					if(success) {
						self.showNotification("category-saved-for-new-item");
						self.$emit("category-saved");
						self.addItem();
					} else {
						if(details === "auth_denied") {
							self.showNotification("auth-denied-error");
						} else {
							self.showNotification("generic-fail");
						}
					}
				});
			} else {
				this.addItem();
			}

		},
		addItem() {
			if(!this.categoryTempId) {
				return;
			}

			//Close other items
			let itemsArray = this.itemsSorted;
			let openItemHeight = 0;
			for(let i = 0; i < itemsArray.length; i++) {
				let item = itemsArray[i];
				if(item.active) {
					this.$refs["category-items-list"].toggleDropdownDelayed(i);
					openItemHeight += this.expandedItemDropdownHeight(item.tempid);
				}
				if(item.editActive) { item.editActive = false; }
			}

			let newItem = this.menuStore.addNewItem(this.categoryTempId);
			newItem.editActive = true;

			//Focus on new item name
			let self = this;

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if(self.$refs) {
						self.$refs["item-editor-" + newItem.tempid].$refs["name-input"].$refs["input"].focus();
					}
					let topOffset = 0;
					if(self.variationsOnly) {
						let firstMenuContainer = document.querySelector('.menu-wrapper-card');
						if (firstMenuContainer && firstMenuContainer?.offsetHeight) {
							topOffset = firstMenuContainer?.offsetHeight;
						}
					}
					this.scrollItemToTop(newItem.tempid, openItemHeight, topOffset);
				});
			});

		},
		expandedItemDropdownHeight(itemTempId) {
			let finalHeight = 0;
			let itemElement = document.querySelector('.category .list-item[data-index="'+itemTempId+'"]');
			finalHeight += itemElement?.offsetHeight || 0;
			finalHeight -= itemElement?.querySelector('.item')?.offsetHeight || 0;
			return finalHeight;
		},
		scrollItemToTop(itemTempId, openItemHeight = 0, addedTopOffset = 0) {
			let itemElement = document.querySelector('.category .list-item[data-index="'+itemTempId+'"]');
			let mainContainer = document.querySelector('.main-content');
			let mainContainerPaddingStyle = window.getComputedStyle(mainContainer, null).getPropertyValue('padding-top');
			let mainContainerPadding = parseInt(mainContainerPaddingStyle) || 0;
			let cardPadding = window.innerWidth <= 768 ? 18 : 24;
			let itemElementOffsetTop = itemElement && itemElement.offsetTop ? itemElement.offsetTop : 0;
			let targetScroll = itemElementOffsetTop + mainContainerPadding + cardPadding - openItemHeight + addedTopOffset - 1;

			mainContainer.scrollTo({
				top: targetScroll,
				behavior: 'smooth'
			});

			clearTimeout(this.scrollItemTimeout);
			this.scrollItemTimeout = setTimeout(()=>{
				if(mainContainer.scrollTop !== targetScroll) {
					mainContainer.scrollTo({
						top: targetScroll,
						behavior: 'smooth'
					});
				}
			}, 550);
		},
		availabilityType(item) {
			if(item.available === false) {
				return 1;
			}
			return 2;
		},
		closeAllItems() {
			this.itemsSorted.forEach(item => {
				if(item.active) {
					let index = this.itemsSorted.indexOf(item);
					this.forceTriggerItem(item, index, true);
				}
			});
		}
	},
	watch: {
		providedAction(newProvidedObject) {
			if(newProvidedObject.hasOwnProperty('multiSelectMode') && newProvidedObject.multiSelectMode !== this.multiSelectMode) {
				this.multiSelectMode = newProvidedObject.multiSelectMode;
				if(this.multiSelectMode) {
					this.closeAllItems();
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.icon-dimension-20 {
	width: 20px;
	height: 20px;
}
.items-container {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 15px 0;
	margin: 0 20px 0 20px;

	:deep(.list-item) {
		padding: 0;
		min-height: unset;
		overflow: hidden;
	}

	:deep(.list-item-add) {
		padding: 8px 12px;

		p.list-item-title {
			font-size: 14px;
		}
	}

	.item-list {
		:deep(.dropdown-content) {
			margin-top: 10px;
			border-bottom: unset;
		}
	}

	.sortable-list-container {
		width: 100%;
		max-width: unset;

		&:deep(.list-item) {
			&:nth-of-type(1) .item.active {
				border-top-left-radius: 13px;
				border-top-right-radius: 13px;
			}
			&:last-of-type .item.active {
				border-bottom-left-radius: 13px;
				border-bottom-right-radius: 13px;
			}
		}

		.item {
			width: 100%;
			display: flex;
			justify-content: space-between;
			user-select: none;
			align-items: center;

			.handle {
				display: flex;
				justify-content: center;
				align-items: center;
				margin-left: 10px;
				transition: opacity 0.3s ease;

				&.undraggable {
					cursor: default;
					pointer-events: none;
					opacity: 0.2;
				}
				img	{
					pointer-events: none;
				}
			}
			.checkbox {
				width: 30px;
				height: 39px;
			}
			&.active {
				background: $alpha_1;
			}

			.item-content {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: calc(100% - 95px);
				padding: 8px 0;
				cursor: pointer;

				.item-name {
					width: calc(100% - 80px);

					.name-wrapper {
						width: fit-content;
						position: relative;
						max-width: 100%;

						&.ticket-item {
							max-width: calc(100% - 25px);
							margin-left: 25px;

							.ticket-icon {
								position: absolute;
								top: 50%;
								left: -25px;
								transform: translateY(-50%);
								width: 20px;
								opacity: .8;
							}
						}
					}
					p {
						@include truncateString(1);
						font-size: 14px;
					}
				}

				.item-price {
					padding: 0 6px;
					width: 80px;

					p {
						@include truncateString(1);
						font-size: 12px;
						text-align: right;
					}

					&.discount {
						p {
							color: var(--color-danger);
							line-height: 12px;
							&.discounted-price {
								color: var(--color-txt);
								text-decoration: line-through;
								font-size: 10px;
								line-height: 11px;
							}
						}
					}
				}
			}

			.item-actions {
				display: flex;
				align-items: center;
				justify-content: center;
				margin-right: 5px;

				&.heading-item-actions {
					width: 42px;
				}

				.availability-dot {
					visibility: hidden;
					margin-left: 5px;

					&.visible {
						visibility: visible;
					}
				}

			}

			&.heading-item {
				background-color: var(--color-bg-primary-2);

				.item-content {
					padding: 0;
					pointer-events: none;

					.item-name p, .item-price p {
						color: var(--txt-2);
						font-size: 10px;
					}
				}
			}
		}
	}

	@media(max-width: 1080px) {
		margin: 0 5px;
	}

}
</style>